import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { createPortal } from 'react-dom';

export default function CreditWarningBanner({ message, onClose, isConnectionError: propIsConnectionError }) {
  const [isVisible, setIsVisible] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isConnectionError, setIsConnectionError] = useState(false);
  const router = useRouter();

  // Effect untuk mounting komponen dan animasi keluar
  useEffect(() => {
    setMounted(true);
    
    // Deteksi ukuran layar untuk menyesuaikan tampilan
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    
    // Periksa saat pertama kali dimuat
    checkMobile();
    
    // PERBAIKAN: Prioritaskan prop, hanya gunakan deteksi pesan sebagai fallback
    console.log('CreditWarningBanner DEBUG - propIsConnectionError:', propIsConnectionError);
    console.log('CreditWarningBanner DEBUG - message:', message);
    
    // SELALU gunakan nilai prop yang diberikan, tanpa kondisi lain
    setIsConnectionError(!!propIsConnectionError);
    console.log('CreditWarningBanner DEBUG - Setting isConnectionError to:', !!propIsConnectionError);
    
    // Tambahkan event listener untuk resize
    window.addEventListener('resize', checkMobile);
    
    // Debug: Tampilkan pesan untuk memastikan komponen dimuat
    console.log('CreditWarningBanner dimuat dengan pesan:', message);
    console.log('Ukuran layar:', window.innerWidth, 'x', window.innerHeight);
    console.log('isMobile:', window.innerWidth <= 480);
    console.log('isConnectionError value:', !!propIsConnectionError);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      setMounted(false);
    };
  }, [message, propIsConnectionError]);

  const handleDismiss = () => {
    console.log('Banner ditutup oleh pengguna');
    setIsVisible(false);
    localStorage.setItem('creditWarningDismissed', Date.now().toString());
    
    // Berikan waktu untuk animasi keluar
    setTimeout(() => {
      if (onClose) onClose();
    }, 400); // Sesuaikan dengan durasi animasi
  };

  const handleUpgrade = () => {
    console.log('Pengguna mengklik Upgrade Paket');
    setIsVisible(false);
    setTimeout(() => {
      router.push('/subscription');
      if (onClose) onClose();
    }, 400); // Sesuaikan dengan durasi animasi
  };

  const handleRetryConnection = () => {
    console.log('Pengguna mengklik Coba Lagi');
    setIsVisible(false);
    window.location.reload();
  };

  if (!isVisible) {
    console.log('Banner tidak ditampilkan karena isVisible = false');
  }

  // Hanya render di browser, bukan server
  if (!mounted) return null;
  
  console.log('RENDER CreditWarningBanner - isConnectionError:', isConnectionError);

  // Gunakan createPortal untuk merender banner di luar hierarki DOM utama
  const bannerContent = (
    <div className={`credit-warning-banner ${!isVisible ? 'closed' : ''}`}
         style={{
           position: 'fixed',
           bottom: isMobile ? '10px' : '20px',
           left: '50%',
           transform: 'translateX(-50%)',
           width: isMobile ? 'calc(100% - 30px)' : 'calc(100% - 60px)',
           maxWidth: '500px',
           backgroundColor: 'rgba(30, 30, 30, 0.95)',
           border: isConnectionError ? '1px solid #e53935' : '1px solid #ffa000',
           borderRadius: '12px',
           padding: isMobile ? '15px' : '20px',
           boxShadow: '0 5px 20px rgba(0, 0, 0, 0.3)',
           zIndex: 9999,
           display: 'flex',
           flexDirection: 'column',
           alignItems: 'center',
           backdropFilter: 'blur(8px)',
           color: 'white',
           fontFamily: 'system-ui, -apple-system, sans-serif',
           animation: !isVisible ? 'slideFadeOut 0.4s ease-out forwards' : 'slideFadeIn 0.4s ease-out',
           boxSizing: 'border-box'
         }}
    >
      <div className="credit-warning-header" style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        marginBottom: isMobile ? '10px' : '15px'
      }}>
        <div className="credit-warning-icon" style={{
          color: isConnectionError ? '#e53935' : '#ffa000',
          marginRight: '12px',
          fontSize: isMobile ? '22px' : '26px',
          display: 'flex',
          alignItems: 'center'
        }}>{isConnectionError ? '🔌' : '⚠️'}</div>
        <h3 className="credit-warning-title" style={{
          color: isConnectionError ? '#e53935' : '#ffa000',
          fontSize: isMobile ? '1rem' : '1.2rem',
          fontWeight: 600,
          margin: 0,
          flexGrow: 1
        }}>{isConnectionError ? 'Masalah Koneksi' : 'Kredit Anda Habis'}</h3>
        <button className="credit-warning-close" 
                onClick={handleDismiss}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  fontSize: '18px',
                  cursor: 'pointer',
                  padding: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>×</button>
      </div>

      <div className="credit-warning-content" style={{
        textAlign: 'center',
        marginBottom: isMobile ? '15px' : '20px',
        color: '#e0e0e0',
        lineHeight: 1.5,
        fontSize: isMobile ? '0.9rem' : '1rem'
      }}>
        {isConnectionError 
          ? message || 'Tidak dapat terhubung ke server. Periksa koneksi internet Anda.' 
          : (message && (message.toLowerCase().includes('koneksi') || message.toLowerCase().includes('terhubung')))
            ? 'Kredit Anda telah habis. Silakan upgrade ke paket berbayar untuk mendapatkan lebih banyak kredit.'
            : message || 'Anda telah kehabisan kredit. Silakan upgrade paket untuk melanjutkan menggunakan layanan.'}
      </div>

      <div className="credit-warning-actions" style={{
        display: 'flex',
        gap: isMobile ? '10px' : '15px',
        width: '100%',
        justifyContent: 'center',
        flexDirection: isMobile && window.innerWidth <= 360 ? 'column' : 'row'
      }}>
        {isConnectionError ? (
          // Tombol untuk error koneksi
          <button className="credit-warning-retry-btn" 
                  onClick={handleRetryConnection}
                  style={{
                    padding: isMobile ? '8px 12px' : '10px 16px',
                    background: '#2196f3',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flexGrow: 1,
                    maxWidth: isMobile && window.innerWidth <= 360 ? '100%' : '160px',
                    fontSize: isMobile ? '0.9rem' : '1rem'
                  }}>
            Coba Lagi
          </button>
        ) : (
          // Tombol untuk kredit habis
          <button className="credit-warning-upgrade-btn" 
                  onClick={handleUpgrade}
                  style={{
                    padding: isMobile ? '8px 12px' : '10px 16px',
                    background: '#ffa000',
                    color: '#000',
                    border: 'none',
                    borderRadius: '8px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    flexGrow: 1,
                    maxWidth: isMobile && window.innerWidth <= 360 ? '100%' : '160px',
                    fontSize: isMobile ? '0.9rem' : '1rem'
                  }}>
            Upgrade Paket
          </button>
        )}
        <button className="credit-warning-dismiss-btn" 
                onClick={handleDismiss}
                style={{
                  padding: isMobile ? '8px 12px' : '10px 16px',
                  background: 'transparent',
                  border: '1px solid #666',
                  color: '#ccc',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  flexGrow: 1,
                  maxWidth: isMobile && window.innerWidth <= 360 ? '100%' : '160px',
                  fontSize: isMobile ? '0.9rem' : '1rem'
                }}>
          {isConnectionError ? 'Tutup' : 'Nanti Saja'}
        </button>
      </div>
    </div>
  );

  // Render ke document body sebagai portal
  return createPortal(
    bannerContent,
    document.body
  );
} 